import type { PatientDraft } from "@/types/patient";

export const EXCEL_MAX_SIZE_BYTES = 5 * 1024 * 1024;
export const EXCEL_MAX_ROWS = 2000;

// Column aliases: header row can be in Spanish (real Excel) or English.
const COLUMN_ALIASES: Record<keyof PatientDraft, string[]> = {
  doc:         ["doc", "documento", "cedula", "cédula", "identificacion", "identificación", "id"],
  nombre:      ["nombre", "paciente", "nombre paciente", "nombre_paciente"],
  cirugia:     ["cirugia", "cirugía", "procedimiento"],
  fecha:       ["fecha", "fecha cirugia", "fecha cirugía", "fecha_cirugia", "fecha_cirugía"],
  telefono:    ["telefono", "teléfono", "celular", "movil", "móvil", "phone"],
  programador: ["programador", "programado por", "programado_por", "programadora"],
};

// Rechaza cadenas que empiezan con caracteres de fórmula (previene CSV/Excel formula injection).
function sanitizeCellValue(v: unknown): string {
  const s = String(v ?? "").trim();
  if (!s) return "";
  if (/^[=+\-@\t\r]/.test(s)) return "'" + s;
  return s;
}

// Convierte fecha Excel (número serial) o string a YYYY-MM-DD.
function toIsoDate(raw: unknown): string {
  if (raw == null || raw === "") return "";
  if (typeof raw === "number") {
    // Excel epoch 1899-12-30 (Lotus bug compat).
    const utcMs = Math.round((raw - 25569) * 86400 * 1000);
    return new Date(utcMs).toISOString().slice(0, 10);
  }
  const s = String(raw).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return m[0];
  const dmy = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
  if (dmy) {
    const [, d, m2, y] = dmy;
    return `${y}-${m2.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  return s;
}

function normalizeHeader(h: string): string {
  return String(h || "").toLowerCase().trim().replace(/\s+/g, " ").replace(/[áàä]/g, "a").replace(/[éèë]/g, "e").replace(/[íìï]/g, "i").replace(/[óòö]/g, "o").replace(/[úùü]/g, "u");
}

export type ParseResult = {
  patients: PatientDraft[];
  warnings: string[];
};

export async function parseExcel(file: File): Promise<ParseResult> {
  if (file.size > EXCEL_MAX_SIZE_BYTES) {
    throw new Error(`Archivo demasiado grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Máximo ${EXCEL_MAX_SIZE_BYTES / 1024 / 1024} MB.`);
  }

  const XLSX = await import("xlsx");
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array", cellDates: false });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) throw new Error("El Excel no tiene hojas.");
  const sheet = workbook.Sheets[sheetName];

  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
    defval: "",
    raw: true,
  });

  if (rows.length > EXCEL_MAX_ROWS) {
    throw new Error(`Excel con demasiadas filas (${rows.length}). Máximo ${EXCEL_MAX_ROWS}.`);
  }

  if (rows.length === 0) {
    return { patients: [], warnings: ["El Excel está vacío."] };
  }

  // Detectar el nombre real de cada columna a partir de los aliases.
  const firstRow = rows[0];
  const headers = Object.keys(firstRow);
  const colMap: Partial<Record<keyof PatientDraft, string>> = {};
  const normHeaders = headers.map((h) => ({ raw: h, norm: normalizeHeader(h) }));

  for (const key of Object.keys(COLUMN_ALIASES) as Array<keyof PatientDraft>) {
    const aliases = COLUMN_ALIASES[key];
    const match = normHeaders.find((h) => aliases.includes(h.norm));
    if (match) colMap[key] = match.raw;
  }

  const warnings: string[] = [];
  if (!colMap.doc)   warnings.push("Columna 'doc/documento/cédula' no encontrada.");
  if (!colMap.fecha) warnings.push("Columna 'fecha' no encontrada.");

  const patients: PatientDraft[] = [];
  for (const row of rows) {
    const doc   = colMap.doc   ? sanitizeCellValue(row[colMap.doc])   : "";
    const fecha = colMap.fecha ? toIsoDate(row[colMap.fecha])         : "";
    if (!doc || !fecha) continue;

    patients.push({
      doc,
      nombre:      colMap.nombre      ? sanitizeCellValue(row[colMap.nombre])      : "",
      cirugia:     colMap.cirugia     ? sanitizeCellValue(row[colMap.cirugia])     : "",
      fecha,
      telefono:    colMap.telefono    ? sanitizeCellValue(row[colMap.telefono])    : "",
      programador: colMap.programador ? sanitizeCellValue(row[colMap.programador]) : "",
    });
  }

  return { patients, warnings };
}
